import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./CategorySelector.css";
import { useApiMutation } from '../../apis/useApi';

interface Category {
    id: string;
    name: string;
    englishName?: string;
}

interface Props {
    setSelectedCategoryName: (name: string) => void;
    setSearchValue: (value: string) => void;
}

const scopeKey = 'BID Partner';

const CategorySelector: React.FC<Props> = ({
    setSelectedCategoryName,
    setSearchValue
}) => {
    const navigate = useNavigate();
    const { categoryId } = useParams();

    const [categories, setCategories] = useState<Category[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const getPartnerOffers = useApiMutation<any, {}>(
        `o/c/partneroffers/scopes/${scopeKey}`,
        'get'
    );

    useEffect(() => {
        const fetchVocabCategories = async () => {
            const query = `{
        taxonomyVocabularies(siteKey: "BID Partner", filter: "name eq 'BID'") {
          items {
            taxonomyCategories {
              items {
                id
                name
              }
            }
          }
        }
      }`;

            try {
                setIsLoading(true);

                // 🔹 Step 1: Get selected category NAMES (English stored)
                const partnersSelectedCategoryMap = new Map<string, string>();

                const response = await getPartnerOffers.mutateAsync({
                    data: {},
                    params: {
                        p_auth: (window as any)?.Liferay?.authToken,
                        pageSize: 0,
                        aggregationTerms:
                            'r_partnerToOffer_c_corporatePartnerId',
                        nestedFields: 'partnerToOffer',
                        filter: "(offerStatus eq 'offerApprovedByAccountManager' or offerStatus eq 'offerApprovedByPartner')"
                    }
                });

                response?.items?.forEach((partner: any) => {
                    const category = partner?.partnersSelectedCategory;
                    if (category) {
                        partnersSelectedCategoryMap.set(category, category);
                    }
                });

                const selectedCategoryNames = Array.from(
                    partnersSelectedCategoryMap.values()
                );

                // 🔹 Step 2: Fetch categories in ENGLISH (for mapping)
                const englishRes = await fetch(
                    '/o/graphql?p_auth=' + (window as any)?.Liferay?.authToken,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'accept-language': 'en-US'
                        },
                        body: JSON.stringify({ query })
                    }
                );

                const englishResult = await englishRes.json();

                const englishCategories =
                    englishResult?.data?.taxonomyVocabularies?.items?.[0]
                        ?.taxonomyCategories?.items ?? [];

                // 🔹 Step 3: Create name → id map
                const nameToIdMap = new Map<string, string>();
                const idToEnglishNameMap = new Map<string, string>();

                englishCategories.forEach((cat: any) => {
                    nameToIdMap.set(cat.name, cat.id);
                    idToEnglishNameMap.set(cat.id, cat.name);
                });

                // 🔹 Step 4: Convert selected names → IDs
                const selectedCategoryIds = selectedCategoryNames
                    .map((name) => nameToIdMap.get(name))
                    .filter(Boolean);

                // 🔹 Step 5: Fetch categories in CURRENT LANGUAGE (for UI)
                const localizedRes = await fetch(
                    '/o/graphql?p_auth=' + (window as any)?.Liferay?.authToken,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'accept-language': (
                                window as any
                            )?.Liferay?.ThemeDisplay?.getBCP47LanguageId()
                        },
                        body: JSON.stringify({ query })
                    }
                );

                const localizedResult = await localizedRes.json();

                const localizedCategories =
                    localizedResult?.data?.taxonomyVocabularies?.items?.[0]
                        ?.taxonomyCategories?.items ?? [];

                // 🔹 Step 6: Filter using IDs (FIXED)
                const items = localizedCategories.filter((item: any) =>
                    selectedCategoryIds.includes(item.id)
                );

                // 🔹 Step 7: Default selection
                if (!categoryId && items.length > 0) {
                    setSelectedCategoryName(items[0].name);
                    sessionStorage.setItem(
                        'selectedCategory',
                        JSON.stringify({
                            name: items[0].name,
                            id: items[0].id,
                            englishName: idToEnglishNameMap.get(items[0].id),
                            locale: (
                                window as any
                            )?.Liferay?.ThemeDisplay?.getBCP47LanguageId()
                        })
                    );
                    navigate(`/categories/${items[0].id}`, { replace: true });
                }

                if (categoryId) {
                    const activeCategory = items.find(
                        (item: any) => item.id === categoryId
                    );

                    if (activeCategory) {
                        setSelectedCategoryName(activeCategory.name);
                        sessionStorage.setItem(
                            'selectedCategory',
                            JSON.stringify({
                                name: activeCategory.name,
                                id: activeCategory.id,
                                englishName: idToEnglishNameMap.get(
                                    activeCategory.id
                                ),
                                locale: (
                                    window as any
                                )?.Liferay?.ThemeDisplay?.getBCP47LanguageId()
                            })
                        );
                    }
                }

                // 🔹 Step 8: Format
                const formattedCategories: Category[] = items.map(
                    (cat: any) => ({
                        id: cat.id,
                        name: cat.name,
                        englishName: idToEnglishNameMap.get(cat.id)
                    })
                );

                setCategories(formattedCategories);
            } catch (err) {
                console.error('Failed to fetch vocab categories', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchVocabCategories();
    }, []);

    const handleClick = (category: Category) => {
        setSelectedCategoryName(category.name);
        setSearchValue('');
        sessionStorage.setItem(
            'selectedCategory',
            JSON.stringify({
                ...category,
                locale: (
                    window as any
                )?.Liferay?.ThemeDisplay?.getBCP47LanguageId()
            })
        );
        navigate(`/categories/${category.id}`);
    };

    if (isLoading) {
        return <div className="category-loading">Loading categories...</div>;
    }

    return (
        <div className="category-container">
            {categories.map((cat) => (
                <button
                    key={cat.id}
                    className={`category-pill ${
                        categoryId === cat.id ? 'active' : ''
                    }`}
                    onClick={() => handleClick(cat)}
                >
                    {cat.name}
                </button>
            ))}
        </div>
    );
};

export default CategorySelector;